//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import ClientId from '@models/ClientId';
import DataSourceId from '@models/DataSourceId';
import Like2BuyV2StoreId from '@models/Like2BuyV2StoreId';
import NetworkUserId from '@models/NetworkUserId';
import { SocialNetwork } from '@models/SocialNetwork';
import { DeepPartial } from '@utils/types/DeepPartial';

export type Like2BuyV2TemplateConfig =
  | ILike2BuyV2TikTokDefaultV1Config
  | ILike2BuyV2InstagramDefaultV1Config;

export interface IFindLike2BuyV2StoresResponse {
  data: ILike2BuyV2Store[];
}

export interface ILike2BuyV2StoreResponse {
  data?: ILike2BuyV2Store;
}

export interface IFindLike2BuyV2StoreIdsResponse {
  data: Like2BuyV2StoreId[];
}

export interface ILike2BuyV2PreviewUrlResponse {
  data: string;
}

export interface ILike2BuyV2Store {
  id: Like2BuyV2StoreId;
  clientId: ClientId;
  dataSourceId: DataSourceId;
  config: Like2BuyV2TemplateConfig;
  metricsName: string;
  network: SocialNetwork;
  networkUserId: NetworkUserId;
  networkUsername: string;
  profileImageUrl?: string;
  shortlink: string;
  isDisabled: boolean;
}

export interface ILike2BuyV2DefaultConfigEmailCapture {
  isEnabled: boolean;
  shouldRequireTermsAndConditions: boolean;
}
export interface ILike2BuyV2TikTokDefaultV1ConfigBanner {
  crawlId?: string;
  imageFileId?: string;
  imageFileName?: string;
  isEnabled: boolean;
}

export interface ILike2BuyV2TikTokDefaultV1Config {
  '@type': 'v1:tiktok-default';
  customStringsByLocale: {
    'en-us'?: DeepPartial<ILike2BuyV2TikTokDefaultV1ConfigCustomStrings>;
  };
  galleryBannerSection?: ILike2BuyV2TikTokDefaultV1ConfigBanner;
  galleryEmailCapture?: ILike2BuyV2DefaultConfigEmailCapture;
}

interface ILike2BuyV2TikTokDefaultV1ConfigCustomStrings {
  gallery: {
    ctaBanner: string;
    emailCapture: {
      cta: string;
      termsAndConditionsUrl: string;
    };
    header: {
      bannerSection: {
        bannerAlt: string;
        destinationUrl: string;
      };
    };
    item: {
      moreInfo: string;
    };
  };
  items: {
    products: {
      header: string;
    };
  };
}

export interface ILike2BuyV2InstagramDefaultV1ConfigBanner {
  crawlId?: string;
  imageFileId?: string;
  imageFileName?: string;
  desktopImageFileId?: string;
  desktopImageFileName?: string;
  isEnabled: boolean;
}

interface ILike2BuyV2InstagramDefaultV1ConfigCustomStrings {
  gallery: {
    ctaBanner: string;
    desktopCtaBanner: string;
    emailCapture: {
      cta: string;
      termsAndConditionsUrl: string;
    };
    header: {
      bannerSection: {
        bannerAlt: string;
        desktopBannerAlt: string;
        destinationUrl: string;
      };
    };
    item: {
      moreInfo: string;
    };
  };
  items: {
    products: {
      header: string;
    };
  };
}

export interface ILike2BuyV2InstagramDefaultV1Config {
  '@type': 'v1:instagram-default';
  customStringsByLocale: {
    'en-us'?: DeepPartial<ILike2BuyV2InstagramDefaultV1ConfigCustomStrings>;
  };
  galleryBannerSection?: ILike2BuyV2InstagramDefaultV1ConfigBanner;
  galleryEmailCapture?: ILike2BuyV2DefaultConfigEmailCapture;
  gallerySquareThumbnails?: {
    isEnabled: boolean;
  };
  gallerySkipDetailsForSingleProduct?: {
    isEnabled: boolean;
  };
}

export function isLike2BuyV2TikTokDefaultConfig(
  config?: Like2BuyV2TemplateConfig,
): config is ILike2BuyV2TikTokDefaultV1Config {
  return config?.['@type'] === 'v1:tiktok-default';
}

export function isLike2BuyV2InstagramDefaultConfig(
  config?: Like2BuyV2TemplateConfig,
): config is ILike2BuyV2InstagramDefaultV1Config {
  return config?.['@type'] === 'v1:instagram-default';
}

export interface IGetLike2BuyV2StoresForClientParams {
  includeDisabled: boolean;
  network?: SocialNetwork;
  networkUserId?: NetworkUserId;
}

export interface IGetLike2BuyV2StoreIdsForClientParams {
  includeDisabled?: boolean;
  network?: SocialNetwork;
  networkUserId?: NetworkUserId;
}
